import type { Guest, RSVPStatus } from '@/types/guest.types';

export type GuestRsvpStatus = 'yes' | 'no' | 'maybe' | 'no_response';
export type GuestRsvpSummary = GuestRsvpStatus | 'mixed';

export interface GuestRow {
  contactId: number;
  userId?: number;
  name: string;
  email?: string;
  phone?: string;
  nickname?: string;
  created_by?: number;
  creatorName?: string;
  creator_nickname?: string;
  hostUserId?: number;
  hostName?: string;
  hostNickname?: string;
  /** Compatibility RSVP summary, not invitation delivery status. */
  invitationStatus: RSVPStatus | 'mixed';
  rsvpStatus?: GuestRsvpSummary;
  isPrimaryGuest?: boolean;
  isAdditionalGuestAllowed?: boolean;
  additionalGuestCount?: number;
  accessToken?: string;
  assignedEventCount: number;
  isInvited: boolean;
  hasResponded: boolean;
  eventGuests: Map<number, Guest | null>;
}

export function normalizeGuestRsvp(status?: string): GuestRsvpStatus {
  switch (status) {
    case 'yes': case 'accepted': return 'yes';
    case 'no': case 'declined': return 'no';
    case 'maybe': return 'maybe';
    default: return 'no_response';
  }
}

export function summarizeGuestRsvp(guests: Iterable<Guest | null>): GuestRsvpSummary {
  const statuses = new Set<GuestRsvpStatus>();
  for (const guest of guests) {
    if (guest) statuses.add(normalizeGuestRsvp(guest.rsvp_status));
  }
  return statuses.size > 1 ? 'mixed' : statuses.values().next().value ?? 'no_response';
}

export function guestRsvpLabel(status?: string): string {
  if (status === 'mixed') return 'Mixed';
  switch (normalizeGuestRsvp(status)) {
    case 'yes': return 'Accepted';
    case 'no': return 'Declined';
    case 'maybe': return 'Maybe';
    default: return 'No response';
  }
}

export function isInvitationRecipientEligible(row: GuestRow): boolean {
  return row.isPrimaryGuest === true && Boolean(row.email?.trim());
}

export function buildCelebrationGuestRows(guests: Guest[]): GuestRow[] {
  const identities = new Map<number, Guest[]>();
  for (const guest of guests) {
    const key = guest.user_id || -guest.id;
    const records = identities.get(key) ?? [];
    records.push(guest);
    identities.set(key, records);
  }

  return Array.from(identities.values(), records => {
    // Celebration roots own identity, eligibility and personal-link data.
    // Stable ordering also makes event-only legacy directories deterministic.
    const ordered = [...records].sort((a, b) =>
      Number(Boolean(a.event_id)) - Number(Boolean(b.event_id)) || a.id - b.id
    );
    const primary = ordered[0];
    const eventGuests = new Map<number, Guest | null>();
    for (const guest of ordered) {
      if (guest.event_id && !eventGuests.has(guest.event_id)) eventGuests.set(guest.event_id, guest);
    }
    const rsvpStatus = summarizeGuestRsvp(eventGuests.values());
    const row: GuestRow = {
      contactId: primary.id,
      userId: primary.user_id || undefined,
      name: primary.name || primary.email || 'Unknown',
      email: primary.email,
      phone: primary.phone,
      nickname: primary.nickname,
      created_by: primary.created_by,
      creatorName: primary.creator_name,
      creator_nickname: primary.creator_nickname,
      hostUserId: primary.host_user_id,
      hostName: primary.host_name || primary.creator_name,
      hostNickname: primary.host_nickname || primary.creator_nickname,
      invitationStatus: rsvpStatus === 'yes' ? 'accepted' : rsvpStatus === 'no' ? 'declined' : rsvpStatus === 'no_response' ? 'new' : rsvpStatus,
      rsvpStatus,
      isPrimaryGuest: primary.is_primary_guest,
      isAdditionalGuestAllowed: primary.is_additional_guest_allowed ?? false,
      additionalGuestCount: primary.additional_guest_count ?? 0,
      accessToken: primary.access_token,
      assignedEventCount: eventGuests.size,
      isInvited: ordered.some(guest => Boolean(guest.invitation_sent_at)),
      hasResponded: rsvpStatus !== 'no_response',
      eventGuests,
    };
    return row;
  }).sort((a, b) => a.name.localeCompare(b.name) || a.contactId - b.contactId);
}